import { fasStar } from 'shared/@generated/icons/fasStar';
import { useId } from 'react';
import { clsx } from 'clsx';
import { Platform, View } from 'react-native';
import { Icon } from 'shared/components/icon';

const STAR_VIEWBOX_SIZE = 24;
const STAR_PATH =
  'M12 1.75L15.09 8.01L22 9.02L17 13.89L18.18 20.75L12 17.5L5.82 20.75L7 13.89L2 9.02L8.91 8.01L12 1.75Z';
const FILLED_STAR_COLOR = '#facc15';
const EMPTY_STAR_COLOR = '#d6d3d1';
const EMPTY_STAR_OPACITY = 1;

function WebRatingStar({ fill, size }: { fill: number; size: number }) {
  const clipPathId = useId().replace(/:/g, '-');
  const clampedFill = Math.max(0, Math.min(1, fill));

  return (
    <svg
      aria-hidden={true}
      width={size}
      height={size}
      viewBox={`0 0 ${STAR_VIEWBOX_SIZE} ${STAR_VIEWBOX_SIZE}`}
      fill={'none'}
      style={{ display: 'block' }}
    >
      <defs>
        <clipPath id={clipPathId}>
          <rect width={STAR_VIEWBOX_SIZE * clampedFill} height={STAR_VIEWBOX_SIZE} />
        </clipPath>
      </defs>

      <path d={STAR_PATH} fill={EMPTY_STAR_COLOR} fillOpacity={EMPTY_STAR_OPACITY} />
      <path d={STAR_PATH} fill={FILLED_STAR_COLOR} clipPath={`url(#${clipPathId})`} />
    </svg>
  );
}

function NativeRatingStar({ fill, sizeClass }: { fill: number; sizeClass: string }) {
  return (
    <View className={clsx('relative', sizeClass)}>
      <Icon icon={fasStar} className={clsx(sizeClass, 'text-brand-cream-darker')} />

      <View
        className={'absolute bottom-0 left-0 top-0 overflow-hidden'}
        style={{ width: `${fill * 100}%` }}
      >
        <Icon icon={fasStar} className={clsx(sizeClass, 'text-yellow-400')} />
      </View>
    </View>
  );
}

export function RatingStars({
  value,
  size = 'md',
  className,
}: {
  value: number;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const nativeStarSizeClass = size === 'sm' ? 'size-4' : 'size-5';
  const starPixelSize = size === 'sm' ? 16 : 20;
  const starOverlap = size === 'sm' ? -1 : -2;

  return (
    <View className={clsx('flex-row items-center', className)}>
      {Array.from({ length: 5 }, (_, index) => {
        const fillPercentage = Math.max(0, Math.min(1, value - index));

        return (
          <View key={index} style={{ marginLeft: index === 0 ? 0 : starOverlap }}>
            {Platform.OS === 'web' ? (
              <WebRatingStar fill={fillPercentage} size={starPixelSize} />
            ) : (
              <NativeRatingStar fill={fillPercentage} sizeClass={nativeStarSizeClass} />
            )}
          </View>
        );
      })}
    </View>
  );
}
